/**
 * This module is used to calculate the layout of the current sub-tree.
 */

import type { ReactElement, ReactNode } from 'react'
import {
  isReactElement,
  buildXMLString,
  normalizeChildren,
  hasDangerouslySetInnerHTMLProp,
} from './utils.js'
import { LayoutNode } from './layout-engine/index.js'
import { getStickyOffset } from './sticky-position.js'
import {
  getBoxShadowExtent,
  getFilterExtent,
  getOutlineExtent,
  isOutside,
  type PaintBounds,
} from './culling.js'
import {
  applyCounters,
  findMarkerTarget,
  getCounterText,
  getListMarker,
  type Counters,
} from './list-marker.js'
import { SVGNodeToImage, serializeSVGFilter } from './handler/preprocess.js'
import { svgTransformToCSS } from './parser/svg-transform.js'
import computeStyle from './handler/compute.js'
import FontLoader from './font.js'
import buildTextNodes from './text/index.js'
import {
  buildInlineText,
  InlineFormatting,
  relativeOffset,
  type AtomicInline,
  type InlineBox,
  type InlineBoxFragment,
  type InlineEnv,
} from './text/inline.js'
import rect from './builder/rect.js'
import outline from './builder/outline.js'
import {
  buildFilterPrimitive,
  getExpansion,
} from './builder/backdrop-filter.js'
import type { BackdropFilter, URLFilter } from './parser/backdrop-filter.js'
import { Locale, normalizeLocale } from './language.js'
import { SerializedStyle } from './handler/expand.js'
import type {
  OuterDisplay,
  ReplacedElementHandlers,
} from './handler/compute.js'
import type { ParsedTransformOrigin } from './transform-origin.js'
import { CONTAINER_PROP } from './handler/stylesheet.js'
import {
  getInputContent,
  getSelectContent,
  PLACEHOLDER_PROP,
} from './form-control.js'
import type { TransformFunction } from './parser/transform.js'
import {
  getDepth,
  isBackFacing,
  resolveTransformState,
  ROOT_TRANSFORM_STATE,
  type ProjectPlane,
  type TransformState,
  default as buildTransform,
} from './builder/transform.js'
import {
  getFixedElementPosition,
  insertFixedNode,
  type FixedContainingBlock,
  type FixedElement,
} from './fixed-position.js'

/** An element drawn in a 3D rendering context, sorted by depth. */
interface Plane {
  depth: number
  svg: string
}

/**
 * Positioned elements and stacking contexts are painted by the stacking
 * context they're in, ordered by `z-index`:
 * https://www.w3.org/TR/CSS22/zindex.html
 */
interface StackingLayer {
  zIndex: number
  svg: string
}

interface StackingContext {
  layers: StackingLayer[]
  /**
   * Whether an element in it has `mix-blend-mode`. They blend with the content
   * of the stacking context only, so it's drawn as an isolated group.
   */
  hasBlending?: boolean
}

/**
 * Paint a stacking context's layers around its in-flow content. Layers are
 * added in tree order, which the sort keeps for equal `z-index` values.
 */
function paintStackingContext(layers: StackingLayer[], content: string) {
  layers.sort((a, b) => a.zIndex - b.zIndex)
  let svg = ''
  let i = 0
  for (; i < layers.length && layers[i].zIndex < 0; i++) svg += layers[i].svg
  svg += content
  for (; i < layers.length; i++) svg += layers[i].svg
  return svg
}

type TransformList = TransformFunction[] & {
  __parent?: TransformList
  __state?: TransformState
}

/** A node, and the position of its border box once it's known. */
export interface PositionedBox {
  node: LayoutNode
  offset: { left: number; top: number }
}

export interface LayoutContext {
  id: string
  parentStyle: SerializedStyle
  inheritedStyle: SerializedStyle
  isInheritingTransform?: boolean
  parent: LayoutNode
  font: FontLoader
  embedFont: boolean
  debug?: boolean
  graphemeImages?: Record<string, string>
  canLoadAdditionalAssets: boolean
  locale?: Locale
  getTwStyles: (tw: string, style: any) => any
  onNodeDetected?: (node: SatoriNode) => void
  onStyleError?: (error: Error) => void
  convertColors: boolean
  pointScaleFactor?: number
  replacedElements?: ReplacedElementHandlers
  /** Draws elements with perspective, see `satori/experimental`. */
  projectPlane?: ProjectPlane
  /** Collects the planes of the 3D rendering context the element is in. */
  planes?: Plane[]
  /**
   * The containing block of `position: fixed` descendants, if they're
   * displayed.
   */
  fixedContainingBlock?: FixedContainingBlock
  /**
   * The containing block of absolutely positioned descendants: the nearest
   * positioned ancestor, or the root.
   */
  absoluteContainingBlock?: FixedContainingBlock
  /** Collects the out-of-flow elements laid out in their containing block. */
  fixedElements: FixedElement[]
  /** The counters in scope, see `list-marker.ts`. */
  counters: Counters
  /** Whether it's in an element with `display: none`, without counters. */
  countersHidden?: boolean
  /** The number of lists the element is in, for their default styles. */
  listDepth?: number
  /** Markers of list items to be placed at the start of its first line. */
  markers?: ReactElement[]
  /** The parent, the containing block of sticky children. */
  parentBox?: PositionedBox
  /**
   * The nearest scroll container, or the root, which sticky descendants stick
   * to.
   */
  scrollport?: PositionedBox
  /**
   * The size of the image, shared by all elements and set once the layout is
   * known. Elements entirely outside of it aren't drawn.
   */
  paintBounds?: PaintBounds
  /** The SVG `<filter>` elements of the document, by their `id`. */
  svgFilters?: Map<string, ReactElement>
  /**
   * Collects the layers of the stacking context the element is in. The root
   * element has none, and establishes the root stacking context.
   */
  stackingContext?: StackingContext
  /**
   * How the parent lays out its children. Elements are blockified unless
   * they're in a block container.
   */
  formattingContext?: 'block' | 'flex' | 'grid' | 'root'
  /** The inline content of the block container the element is in. */
  inline?: InlineFormatting
  /** Lays out a tree on its own, for atomic inlines. */
  computeLayout?: InlineEnv['computeLayout']
  /** The node of a paragraph to lay out text in, see `buildInlineText()`. */
  textNode?: LayoutNode
  /** Whether there are floats, which text wraps around. */
  floats?: { found: boolean }
  /** Whether the text fills the width of a block container. */
  blockParagraph?: boolean
  /** The nodes of query containers, by their `CONTAINER_PROP`. */
  containers?: Map<number, LayoutNode>
}

export interface SatoriNode {
  // Layout information.
  left: number
  top: number
  width: number
  height: number
  type: string
  key?: string | number
  props: Record<string, any>
  textContent?: string
}

/**
 * The border box of a fragment of an inline box, offset by `left` and `top`,
 * the position of its block container.
 */
function fragmentRect(
  box: InlineBox,
  fragment: InlineBoxFragment,
  left: number,
  top: number
) {
  const paragraph = fragment.paragraph.node.layout
  const [dx, dy] = relativeOffset([...box.ancestors, box], paragraph.width)
  return {
    left: left + paragraph.left + fragment.left + dx,
    top: top + paragraph.top + fragment.top + dy,
    width: fragment.width,
    height: fragment.height,
  }
}

/**
 * Reports the descendants of an element that isn't displayed, which aren't
 * laid out, with empty boxes like in browsers. Like in `layout()`, fragments
 * are `display: contents` elements.
 */
function reportHiddenDescendants(children: ReactNode, context: LayoutContext) {
  const { onNodeDetected, replacedElements } = context
  for (const child of normalizeChildren(children)) {
    if (!isReactElement(child)) continue
    const element = child as ReactElement
    const isFragment =
      (element.type as unknown) === Symbol.for('react.fragment')
    const {
      children: descendants,
      [CONTAINER_PROP]: _container,
      [PLACEHOLDER_PROP]: _placeholder,
      ...props
    } = element.props || {}
    const type = isFragment ? 'div' : (element.type as string)
    onNodeDetected?.({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      type,
      props: isFragment ? { style: { display: 'contents' } } : props,
      key: element.key ?? undefined,
      textContent: isReactElement(descendants) ? undefined : descendants,
    })
    // Like when they're displayed, the content of SVG and replaced elements
    // isn't reported.
    if (type !== 'svg' && !replacedElements?.[type]) {
      reportHiddenDescendants(descendants, context)
    }
  }
}

export default async function* layout(
  element: ReactNode,
  context: LayoutContext
): AsyncGenerator<
  { word: string; locale?: string }[],
  string,
  [number, number]
> {
  const {
    id,
    inheritedStyle,
    parent,
    font,
    debug,
    locale,
    embedFont = true,
    graphemeImages,
    canLoadAdditionalAssets,
    getTwStyles,
  } = context

  // 1. Pre-process the node.
  if (element === null || typeof element === 'undefined') {
    yield
    yield
    return ''
  }

  // A text node. In a block container, it's inline content. Components are
  // already rendered, see `resolveComponents()`.
  if (!isReactElement(element)) {
    const iter = context.inline
      ? buildInlineText(String(element), context)
      : buildTextNodes(String(element), context)
    yield (await iter.next()).value as { word: string; locale?: Locale }[]
    await iter.next()
    const offset = yield
    return (await iter.next(offset)).value as string
  }

  // A fragment, e.g. returned by a component, generates no box.
  if ((element.type as unknown) === Symbol.for('react.fragment')) {
    return yield* layout(
      {
        ...element,
        type: 'div',
        props: {
          children: element.props?.children,
          style: { display: 'contents' },
        },
      } as ReactElement,
      context
    )
  }

  // Process as element.
  const { type: $type, props } = element
  // type must be a string here.
  const type = $type as string

  if (props && hasDangerouslySetInnerHTMLProp(props)) {
    throw new Error(
      'dangerouslySetInnerHTML property is not supported. See documentation for more information https://github.com/vercel/satori#jsx.'
    )
  }
  // A line break in inline content.
  if (type === 'br') {
    context.inline?.addBreak()
    yield []
    yield
    return ''
  }

  let { style, children, tw, lang: _newLocale = locale } = props || {}
  const newLocale = normalizeLocale(_newLocale)

  // Extend Tailwind styles. Like with Tailwind's preflight styles, elements
  // have `border-box` sizing and solid borders without a width.
  if (tw) {
    const twStyles = getTwStyles(tw, style)
    style = Object.assign(
      { boxSizing: 'border-box', borderWidth: 0, borderStyle: 'solid' },
      twStyles,
      style
    )
  }

  // The `transform` attribute of an inline SVG transforms its box, unless the
  // `transform` property is set.
  if (
    type === 'svg' &&
    typeof props.transform === 'string' &&
    style?.transform === undefined
  ) {
    const transform = svgTransformToCSS(props.transform)
    if (transform) style = { ...style, transform }
  }

  const node = new LayoutNode()
  // Container queries are evaluated with the sizes of these, see `satori()`.
  if (typeof props?.[CONTAINER_PROP] === 'number') {
    context.containers?.set(props[CONTAINER_PROP], node)
  }

  const [computedStyle, newInheritableStyle] = await computeStyle(
    node,
    type,
    inheritedStyle,
    style,
    props,
    context.replacedElements,
    context.onStyleError,
    context.convertColors,
    context.pointScaleFactor,
    context.listDepth
  )
  // For the `ch` unit of descendants and of later properties, in the font of
  // the element.
  const chRatio = font.getChRatio(computedStyle as any)
  if (chRatio !== undefined) {
    computedStyle._chRatio = newInheritableStyle._chRatio = chRatio
  }
  // Inputs show their value or placeholder instead of children, and selects
  // their options.
  if (type === 'input') children = getInputContent(props, computedStyle, font)
  else if (type === 'select') children = getSelectContent(props, computedStyle)

  // Counters are changed in tree order, by elements that are displayed.
  const countersHidden =
    context.countersHidden || computedStyle.__outerDisplay === 'none'
  if (!countersHidden) {
    applyCounters(
      context.counters,
      computedStyle,
      id.slice(0, Math.max(0, id.lastIndexOf('-'))),
      [].concat(children ?? []).flat()
    )
  }

  // Counters in the content of pseudo-elements have their values at this
  // point of the tree, after the counters they change, see
  // `applyStyleSheets()`.
  if (type === '::before' || type === '::after') {
    children = normalizeChildren(children).map((child) =>
      child?.type === '::counter'
        ? getCounterText(
            context.counters,
            child.props.name,
            child.props.separator,
            child.props.counterStyle
          )
        : child
    )
  }

  // Elements are blockified in flex and grid containers, as the root
  // element, and when they're out of flow. Inline elements without a box are
  // laid out by the paragraphs of their block container, and atomic inlines
  // on their own.
  const isOutOfFlow =
    computedStyle.position === 'absolute' || computedStyle.position === 'fixed'
  let outerDisplay = computedStyle.__outerDisplay as OuterDisplay
  // Floats only apply in block containers, and are blockified too.
  if (
    node.style.float &&
    (context.formattingContext !== 'block' ||
      !context.inline ||
      outerDisplay === 'contents' ||
      node.style.display === 'none')
  ) {
    node.style.float = undefined
  }
  const isFloat = !!node.style.float
  if (
    outerDisplay === 'inline' &&
    (context.formattingContext !== 'block' ||
      isOutOfFlow ||
      isFloat ||
      !context.inline)
  ) {
    outerDisplay = 'block'
  }
  const isReplacedElement =
    type === 'img' || type === 'svg' || !!context.replacedElements?.[type]
  const isInlineBox =
    outerDisplay === 'inline' &&
    computedStyle.__innerDisplay === 'inline' &&
    !isReplacedElement
  const atomicInline: AtomicInline | undefined =
    outerDisplay === 'inline' && !isInlineBox
      ? { node, style: computedStyle }
      : undefined
  const isContents = outerDisplay === 'contents' && !!context.inline
  let inlineBox: InlineBox | undefined
  if (isInlineBox) {
    // `transform` and `overflow` don't apply to inline boxes.
    computedStyle.transform = inheritedStyle.transform
    newInheritableStyle.transform = inheritedStyle.transform
    computedStyle.overflow = 'visible'
    computedStyle.overflowX = computedStyle.overflowY = 'visible'
    computedStyle.clipPath = undefined
    inlineBox = { style: computedStyle, fragments: [], paragraphs: [] }
  }

  // Absolutely positioned and fixed elements are laid out in their containing
  // block. Without a box, they're not positioned.
  const outOfFlowContainingBlock =
    node.style.display === 'none' || node.style.display === 'contents'
      ? undefined
      : computedStyle.position === 'fixed'
      ? context.fixedContainingBlock
      : computedStyle.position === 'absolute'
      ? context.absoluteContainingBlock
      : undefined
  const fixedElement = outOfFlowContainingBlock
    ? insertFixedNode(
        node,
        parent,
        outOfFlowContainingBlock,
        context.fixedElements
      )
    : undefined
  if (fixedElement) {
    // `overflow: hidden` of elements between it and its containing block
    // doesn't clip it.
    const { clipPathId, maskId } = fixedElement.containingBlock
    for (const s of [computedStyle, newInheritableStyle]) {
      s._inheritedClipPathId = clipPathId
      s._inheritedMaskId = maskId
    }
  } else if (inlineBox) {
    context.inline.openBox(inlineBox)
  } else if (atomicInline) {
    context.inline.addAtomic(atomicInline)
  } else if (isFloat) {
    context.inline.addFloat(node)
    if (context.floats) context.floats.found = true
  } else if (!isContents) {
    // A block-level box ends the paragraph before it.
    if (outerDisplay === 'block' && !isOutOfFlow) {
      context.inline?.breakForBlock()
    }
    parent.insertChild(node)
  }

  // Post-process styles to attach inheritable properties for Satori.

  const opacity =
    typeof computedStyle.opacity === 'number' ? computedStyle.opacity : 1

  // Elements that affect how their children are transformed, and children of
  // those, need their own transform state even without a `transform`. Like in
  // browsers, `opacity` makes `preserve-3d` flat, as the element and its
  // descendants are drawn as a group.
  const preserve3d =
    computedStyle.transformStyle === 'preserve-3d' && opacity === 1
  const perspective =
    typeof computedStyle.perspective === 'number'
      ? computedStyle.perspective
      : undefined

  // These properties make the element the containing block of its fixed
  // descendants.
  const isFixedContainingBlock =
    computedStyle.transformStyle === 'preserve-3d' ||
    perspective !== undefined ||
    (computedStyle.transform !== inheritedStyle.transform &&
      (computedStyle.transform as unknown as TransformList).length > 0) ||
    (!!computedStyle.filter &&
      computedStyle.filter !== 'none' &&
      computedStyle.filter !== inheritedStyle.filter) ||
    (computedStyle._backdropFilters as unknown as unknown[] | undefined)
      ?.length > 0

  if (
    computedStyle.transform === inheritedStyle.transform &&
    (preserve3d ||
      perspective !== undefined ||
      context.parentStyle.transformStyle === 'preserve-3d' ||
      typeof context.parentStyle.perspective === 'number')
  ) {
    computedStyle.transform = [] as any
    newInheritableStyle.transform = computedStyle.transform
  }

  // If the element is inheriting the parent `transform`, or applying its own.
  // This affects the coordinate system.
  const isInheritingTransform =
    computedStyle.transform === inheritedStyle.transform
  if (!isInheritingTransform) {
    ;(computedStyle.transform as unknown as TransformList).__parent =
      inheritedStyle.transform as unknown as TransformList
  }

  // Transformed elements, and their descendants, can be drawn elsewhere than
  // their boxes, so they're always drawn, even when their boxes are outside of
  // the image. Filters can draw outside of what they filter, e.g. with
  // `drop-shadow()`, so they're checked with a margin.
  if (!isInheritingTransform) newInheritableStyle._alwaysDrawn = 1
  const filterExtent = getFilterExtent(
    computedStyle._filters as unknown as
      | (BackdropFilter | URLFilter)[]
      | undefined
  )
  if (filterExtent) {
    newInheritableStyle._cullMargin =
      ((inheritedStyle._cullMargin as number) || 0) + filterExtent
  }

  // A `preserve-3d` element establishes a 3D rendering context, or extends
  // the one it's in. Its children are drawn sorted by depth.
  const planes = preserve3d ? context.planes || [] : undefined

  // If the element has `overflow` set to `hidden` or clip-path is set, we need to create a clip
  // path and use it in all its children.
  const hasClipPath =
    computedStyle.clipPath && computedStyle.clipPath !== 'none'
  if (computedStyle.overflow === 'hidden' || hasClipPath) {
    newInheritableStyle._inheritedClipPathId = `satori_cp-${id}`
    newInheritableStyle._inheritedMaskId = `satori_om-${id}`
  }

  if (computedStyle.maskImage) {
    newInheritableStyle._inheritedMaskId = `satori_mi-${id}`
  }

  // Fixed descendants are clipped by their containing block, and by the
  // `clip-path` and `mask-image` of elements in between. Descendants of an
  // element that isn't displayed aren't displayed either.
  let fixedContainingBlock = context.fixedContainingBlock
  const fixedClip = {
    clipPathId: newInheritableStyle._inheritedClipPathId as string | undefined,
    maskId: newInheritableStyle._inheritedMaskId as string | undefined,
  }
  if (node.style.display === 'none') {
    fixedContainingBlock = undefined
  } else if (isFixedContainingBlock) {
    fixedContainingBlock = { node, offset: { left: 0, top: 0 }, ...fixedClip }
  } else if (fixedContainingBlock && (hasClipPath || computedStyle.maskImage)) {
    fixedContainingBlock = { ...fixedContainingBlock, ...fixedClip }
  }
  // Positioned elements are the containing block of absolutely positioned
  // descendants too, unless they don't have a box in the layout.
  let absoluteContainingBlock = context.absoluteContainingBlock
  if (node.style.display === 'none') {
    absoluteContainingBlock = undefined
  } else if (fixedContainingBlock?.node === node) {
    absoluteContainingBlock = fixedContainingBlock
  } else if (
    computedStyle.position !== 'static' &&
    !inlineBox &&
    outerDisplay !== 'contents'
  ) {
    absoluteContainingBlock = {
      node,
      offset: { left: 0, top: 0 },
      ...fixedClip,
    }
  } else if (
    absoluteContainingBlock &&
    (hasClipPath || computedStyle.maskImage)
  ) {
    absoluteContainingBlock = { ...absoluteContainingBlock, ...fixedClip }
  }

  // The containing block of sticky children is the nearest block container,
  // and they stick to the nearest scroll container.
  const box: PositionedBox = { node, offset: { left: 0, top: 0 } }
  const isScrollContainer =
    !inlineBox &&
    [computedStyle.overflowX, computedStyle.overflowY].some(
      (overflow) =>
        overflow === 'hidden' || overflow === 'scroll' || overflow === 'auto'
    )
  const parentBox = inlineBox || isContents ? context.parentBox : box
  const scrollport = isScrollContainer ? box : context.scrollport

  // A stacking context paints its descendants together. Elements of a 3D
  // rendering context, elements projected by the `perspective` of their parent,
  // and elements hidden by `backface-visibility` are drawn with their
  // descendants too. `z-index` applies to positioned elements, and to flex and
  // grid items.
  const isPositioned = computedStyle.position !== 'static'
  const zIndex =
    typeof computedStyle.zIndex === 'number' &&
    (isPositioned ||
      context.formattingContext === 'flex' ||
      context.formattingContext === 'grid')
      ? computedStyle.zIndex
      : undefined
  const mixBlendMode =
    computedStyle.mixBlendMode && computedStyle.mixBlendMode !== 'normal'
      ? computedStyle.mixBlendMode
      : undefined
  const isStackingContext =
    !context.stackingContext ||
    !!mixBlendMode ||
    computedStyle.isolation === 'isolate' ||
    zIndex !== undefined ||
    computedStyle.position === 'fixed' ||
    computedStyle.position === 'sticky' ||
    isFixedContainingBlock ||
    opacity < 1 ||
    !!hasClipPath ||
    !!computedStyle.maskImage ||
    !!context.planes ||
    typeof context.parentStyle.perspective === 'number' ||
    computedStyle.backfaceVisibility === 'hidden'
  const stackingContext: StackingContext = isStackingContext
    ? { layers: [] }
    : context.stackingContext
  if (mixBlendMode && context.stackingContext) {
    context.stackingContext.hasBlending = true
  }

  // If the element has `background-clip: text` set, we need to create a clip
  // path and use it in all its children.
  if (computedStyle.backgroundClip === 'text') {
    const mutateRefValue = { value: '' } as any
    newInheritableStyle._inheritedBackgroundClipTextPath = mutateRefValue
    computedStyle._inheritedBackgroundClipTextPath = mutateRefValue

    if (computedStyle.backgroundImage) {
      newInheritableStyle._inheritedBackgroundClipTextHasBackground = 'true'
      computedStyle._inheritedBackgroundClipTextHasBackground = 'true'
    }
  }

  // 2. Do layout recursively for its children.
  // Children of replaced elements, e.g. the fallback content of a <canvas>,
  // are never rendered, and neither are the ones of elements that aren't
  // displayed, e.g. the text of <style>, which would be laid out for nothing.
  // The content of SVG elements is drawn as an image.
  const isReplaced = !!context.replacedElements?.[type]
  const normalizedChildren =
    isReplaced || type === 'svg' || node.style.display === 'none'
      ? []
      : normalizeChildren(children)
  const iterators: ReturnType<typeof layout>[] = []

  // The markers of list items are placed at the start of the first line of
  // their block container: before its first in-flow child, or in the first
  // line of that child if it's a block container too.
  let markers = context.markers
  if (computedStyle.__listItem && !countersHidden && !isReplaced) {
    const engine = font.getEngine(
      computedStyle.fontSize as number,
      computedStyle.lineHeight as number,
      computedStyle as any,
      newLocale
    )
    markers = [
      ...(markers || []),
      ...getListMarker(
        computedStyle,
        context.counters.value('list-item'),
        Math.round(engine.ascent('0'))
      ),
    ]
  }
  let markerTarget = -1
  if (markers?.length && !isReplaced && type !== 'svg') {
    const isItemContainer =
      computedStyle.__innerDisplay === 'flex' ||
      computedStyle.__innerDisplay === 'grid'
    const target = findMarkerTarget(normalizedChildren, isItemContainer)
    if (target.nested) {
      markerTarget = target.index
    } else if (target.wrap) {
      normalizedChildren[target.index] = {
        type: 'span',
        key: null,
        props: { children: [...markers, normalizedChildren[target.index]] },
      } as any
    } else if (!isItemContainer) {
      normalizedChildren.splice(target.index, 0, ...markers)
    }
  }

  // Children of a block container take part in its inline content, and
  // children of inline boxes in the one of their block container.
  const isBlockContainer =
    !inlineBox &&
    !isContents &&
    !isReplacedElement &&
    (node.style.display === 'block' || node.style.display === 'flow-root')
  const childInline = isBlockContainer
    ? new InlineFormatting(node, computedStyle, {
        font,
        embedFont,
        debug,
        graphemeImages,
        pointScaleFactor: context.pointScaleFactor,
        computeLayout: context.computeLayout,
      })
    : inlineBox || isContents
    ? context.inline
    : undefined
  const childFormattingContext: LayoutContext['formattingContext'] =
    isBlockContainer
      ? 'block'
      : inlineBox || outerDisplay === 'contents'
      ? context.formattingContext
      : node.style.display === 'grid'
      ? 'grid'
      : 'flex'
  const childParent = inlineBox || isContents ? context.inline.container : node

  let i = 0
  const segmentsMissingFont: { word: string; locale?: string }[] = []
  // The `order` of the flex or grid item each child generates.
  const childOrders: number[] = []
  for (const [index, child] of normalizedChildren.entries()) {
    // In flex and grid containers, text with only white space isn't
    // rendered, even with `white-space: pre`, e.g. the newlines between the
    // lines of code blocks.
    if (
      typeof child === 'string' &&
      (childFormattingContext === 'flex' ||
        childFormattingContext === 'grid') &&
      /^[ \t\n\r\f]*$/.test(child)
    ) {
      continue
    }
    const childCount = childParent.children.length
    const iter = layout(child, {
      id: id + '-' + i++,
      parentStyle: computedStyle,
      inheritedStyle: newInheritableStyle,
      isInheritingTransform: true,
      parent: childParent,
      font,
      embedFont,
      debug,
      graphemeImages,
      canLoadAdditionalAssets,
      locale: newLocale,
      getTwStyles,
      onNodeDetected: context.onNodeDetected,
      onStyleError: context.onStyleError,
      convertColors: context.convertColors,
      pointScaleFactor: context.pointScaleFactor,
      replacedElements: context.replacedElements,
      projectPlane: context.projectPlane,
      planes,
      fixedContainingBlock,
      absoluteContainingBlock,
      fixedElements: context.fixedElements,
      paintBounds: context.paintBounds,
      svgFilters: context.svgFilters,
      counters: context.counters,
      countersHidden,
      listDepth:
        (context.listDepth || 0) +
        (['ul', 'ol', 'menu', 'dir'].includes(type) ? 1 : 0),
      markers: index === markerTarget ? markers : undefined,
      parentBox,
      scrollport,
      floats: context.floats,
      stackingContext,
      formattingContext: childFormattingContext,
      inline: childInline,
      computeLayout: context.computeLayout,
      containers: context.containers,
    })
    if (canLoadAdditionalAssets) {
      segmentsMissingFont.push(...(((await iter.next()).value as any) || []))
    } else {
      await iter.next()
    }
    const added = childParent.children.slice(childCount)
    childOrders.push(added.length === 1 ? added[0].style.order || 0 : 0)
    iterators.push(iter)
  }
  if (isBlockContainer) childInline.end()
  if (inlineBox) context.inline.closeBox(inlineBox)
  // Counters created by the children are out of scope.
  context.counters.closeScope(id)
  yield segmentsMissingFont
  for (const iter of iterators) await iter.next()

  // 3. Post-process the node.
  const [x, y] = yield
  // Atomic inlines are positioned by their paragraph.
  atomicInline?.paragraph?.finalize()
  let { left, top, width, height } = node.layout
  if (fixedElement) {
    ;[left, top] = getFixedElementPosition(fixedElement, x, y)
  } else {
    // Attach offset to the current node.
    left += x
    top += y
  }
  if (
    computedStyle.position === 'sticky' &&
    !inlineBox &&
    context.parentBox &&
    context.scrollport
  ) {
    const [dx, dy] = getStickyOffset(
      { left, top, width, height, margin: node.layout.margin },
      computedStyle,
      context.parentBox,
      context.scrollport
    )
    left += dx
    top += dy
  }
  box.offset.left = left
  box.offset.top = top
  for (const containingBlock of [
    fixedContainingBlock,
    absoluteContainingBlock,
  ]) {
    if (containingBlock?.node === node) {
      containingBlock.offset.left = left
      containingBlock.offset.top = top
    }
  }

  // Add the layer before the descendants add theirs, to keep the tree order.
  // Elements of a 3D rendering context are drawn by depth instead. Floats
  // are drawn over the in-flow blocks around them.
  let layer: StackingLayer | undefined
  if (
    context.stackingContext &&
    !context.planes &&
    (isPositioned || isStackingContext || isFloat)
  ) {
    layer = { zIndex: zIndex ?? 0, svg: '' }
    context.stackingContext.layers.push(layer)
  }

  let childrenRenderResult = ''
  let baseRenderResult = ''
  let depsRenderResult = ''

  // Emit event for the current node. We don't pass the children prop to the
  // event handler because everything is already flattened, unless it's a text
  // node.
  const {
    children: childrenNode,
    [CONTAINER_PROP]: _container,
    [PLACEHOLDER_PROP]: _placeholder,
    ...restProps
  } = props
  // Inline boxes are reported like by `getBoundingClientRect()` in browsers,
  // as the union of the border boxes of their fragments on each line.
  let detected = { left, top, width, height }
  if (inlineBox) {
    for (const paragraph of inlineBox.paragraphs) paragraph.finalize()
    const rects = inlineBox.fragments.map((fragment) =>
      fragmentRect(inlineBox, fragment, left, top)
    )
    if (rects.length) {
      const minLeft = Math.min(...rects.map((r) => r.left))
      const minTop = Math.min(...rects.map((r) => r.top))
      detected = {
        left: minLeft,
        top: minTop,
        width: Math.max(...rects.map((r) => r.left + r.width)) - minLeft,
        height: Math.max(...rects.map((r) => r.top + r.height)) - minTop,
      }
    }
  }
  context.onNodeDetected?.({
    ...detected,
    type,
    props: restProps,
    key: element.key,
    textContent: isReactElement(childrenNode) ? undefined : childrenNode,
  })
  if (node.style.display === 'none' && context.onNodeDetected) {
    reportHiddenDescendants(childrenNode, context)
  }

  // Resolve transforms now that the layout is known. Children read the state
  // from their parent's transform list.
  const transformList = computedStyle.transform as unknown as
    | TransformList
    | undefined
  if (transformList && !isInheritingTransform) {
    transformList.__state = resolveTransformState({
      functions: transformList,
      box: { left, top, width, height },
      origin: computedStyle.transformOrigin as ParsedTransformOrigin,
      parent: transformList.__parent?.__state || ROOT_TRANSFORM_STATE,
      preserve3d,
      perspectiveDistance: perspective,
      perspectiveOrigin:
        computedStyle.perspectiveOrigin as unknown as ParsedTransformOrigin,
      canProject: !!context.projectPlane,
    })
  }
  const transformState = transformList?.__state || ROOT_TRANSFORM_STATE
  const isHidden =
    transformState.behindViewer ||
    (computedStyle.backfaceVisibility === 'hidden' &&
      isBackFacing(transformState))

  // Planes of a 3D rendering context are drawn back to front. Equal depths
  // keep the document order, so an element is added before its children.
  let plane: Plane | undefined
  const contextPlanes = context.planes || planes
  if (contextPlanes) {
    const depth = getDepth(transformState, left + width / 2, top + height / 2)
    // Round off floating point errors, so planes at the same depth are equal.
    plane = {
      depth: Number.isFinite(depth) ? Math.round(depth * 1e6) / 1e6 : 0,
      svg: '',
    }
    contextPlanes.push(plane)
  }

  // An element is drawn as a group with its opacity, with its descendants
  // and the elements in its stacking context. Without
  // children, it's drawn by itself, which avoids an extra group for a single
  // shape.
  let groupOpacity = opacity < 1 && iterators.length > 0 ? opacity : 1
  const rectOpacity = groupOpacity < 1 ? 1 : opacity
  const drawn = { shapes: true }

  // Boxes entirely outside of the image aren't drawn, with their shadows,
  // unless they define the clip paths or masks of their descendants. The
  // descendants are checked by themselves.
  const paintBounds = newInheritableStyle._alwaysDrawn
    ? undefined
    : context.paintBounds
  const definesClip =
    newInheritableStyle._inheritedClipPathId !==
      inheritedStyle._inheritedClipPathId ||
    newInheritableStyle._inheritedMaskId !== inheritedStyle._inheritedMaskId
  const cullMargin = (newInheritableStyle._cullMargin as number) || 0
  const shadowExtent =
    paintBounds && !definesClip
      ? getBoxShadowExtent(computedStyle) + cullMargin
      : 0
  const isCulled = (area: {
    left: number
    top: number
    width: number
    height: number
  }) =>
    !!paintBounds &&
    !definesClip &&
    isOutside(
      paintBounds,
      area.left,
      area.top,
      area.width,
      area.height,
      shadowExtent
    )
  const isBoxCulled = isCulled({ left, top, width, height })

  // Generate the rendered markup for the current node.
  if ((type === 'img' || isReplaced || type === 'svg') && isBoxCulled) {
    // Not drawn, see above.
  } else if (type === 'img' || isReplaced) {
    // A replaced element without rendered content has no `src`, so it's drawn
    // as a box.
    const src = computedStyle.__src as string | undefined
    baseRenderResult = await rect(
      {
        id,
        left,
        top,
        width,
        height,
        src,
        isInheritingTransform,
        debug,
        opacity: rectOpacity,
      },
      computedStyle,
      newInheritableStyle
    )
  } else if (type === 'svg') {
    // When entering a <svg> node, we need to convert it to a <img> with the
    // SVG data URL embedded.
    const currentColor = computedStyle.color
    // `var()` in its attributes uses the variables of the element.
    const variables: Record<string, string> = {}
    for (const key in computedStyle) {
      if (key.startsWith('--')) variables[key] = String(computedStyle[key])
    }
    const src = await SVGNodeToImage(
      element,
      currentColor,
      context.convertColors,
      variables
    )
    // Like in browsers, the viewBox is fitted in the box by the
    // `preserveAspectRatio` of the SVG, which the image applies to it.
    const aspectRatio = (element.props as Record<string, unknown>)
      ?.preserveAspectRatio
    baseRenderResult = await rect(
      {
        id,
        left,
        top,
        width,
        height,
        src,
        svgAspectRatio:
          typeof aspectRatio === 'string' && aspectRatio.trim()
            ? aspectRatio.trim()
            : 'xMidYMid meet',
        isInheritingTransform,
        debug,
        opacity: rectOpacity,
      },
      computedStyle,
      newInheritableStyle
    )
  } else if (inlineBox) {
    // Each line has a fragment of the box. The edges are only drawn where the
    // box starts and ends.
    for (const paragraph of inlineBox.paragraphs) paragraph.finalize()
    drawn.shapes = false
    for (const [index, fragment] of inlineBox.fragments.entries()) {
      const fragmentStyle = { ...computedStyle }
      if (!fragment.first) {
        fragmentStyle.borderLeftWidth = 0
        fragmentStyle.paddingLeft = 0
        fragmentStyle.borderTopLeftRadius = 0
        fragmentStyle.borderBottomLeftRadius = 0
      }
      if (!fragment.last) {
        fragmentStyle.borderRightWidth = 0
        fragmentStyle.paddingRight = 0
        fragmentStyle.borderTopRightRadius = 0
        fragmentStyle.borderBottomRightRadius = 0
      }
      const fragmentBox = fragmentRect(inlineBox, fragment, left, top)
      if (isCulled(fragmentBox)) continue
      const fragmentDrawn = { shapes: true }
      baseRenderResult += await rect(
        {
          id: `${id}-${index}`,
          ...fragmentBox,
          isInheritingTransform,
          debug,
          opacity: rectOpacity,
          drawn: fragmentDrawn,
        },
        fragmentStyle,
        newInheritableStyle
      )
      drawn.shapes ||= fragmentDrawn.shapes
    }
  } else if (!isBoxCulled) {
    baseRenderResult = await rect(
      {
        id,
        left,
        top,
        width,
        height,
        isInheritingTransform,
        debug,
        opacity: rectOpacity,
        drawn,
      },
      computedStyle,
      newInheritableStyle
    )
  }

  // If the element only has text, the text is drawn with the opacity, which
  // can avoid a group for a single path.
  if (
    groupOpacity < 1 &&
    !drawn.shapes &&
    normalizedChildren.length === 1 &&
    typeof normalizedChildren[0] === 'string'
  ) {
    computedStyle._textOpacity = groupOpacity
    groupOpacity = 1
  }

  // Generate the rendered markup for the children. Flex and grid items are
  // painted in `order`, like they're laid out.
  const paintOrder = iterators.map((_, index) => index)
  if (
    childParent === node &&
    (node.style.display === 'flex' || node.style.display === 'grid') &&
    childOrders.some(Boolean)
  ) {
    paintOrder.sort((a, b) => childOrders[a] - childOrders[b])
  }
  for (const index of paintOrder) {
    childrenRenderResult += (await iterators[index].next([left, top])).value
  }

  // The outline is drawn over the element and its in-flow descendants, and
  // under positioned ones. Outlines of inline boxes aren't drawn.
  const outlineResult =
    inlineBox ||
    computedStyle.visibility === 'hidden' ||
    (paintBounds &&
      isOutside(
        paintBounds,
        left,
        top,
        width,
        height,
        getOutlineExtent(computedStyle) + cullMargin
      ))
      ? ''
      : outline(
          { id, left, top, width, height, isInheritingTransform },
          computedStyle
        )

  // An extra pass to generate the special background-clip shape collected from
  // children.
  if (computedStyle._inheritedBackgroundClipTextPath) {
    depsRenderResult += buildXMLString(
      'clipPath',
      {
        id: `satori_bct-${id}`,
        'clip-path': computedStyle._inheritedClipPathId
          ? `url(#${computedStyle._inheritedClipPathId})`
          : undefined,
      },
      (computedStyle._inheritedBackgroundClipTextPath as any).value
    )
  }

  // Children in the same 3D rendering context were added to `planes` and
  // returned nothing, so this is the element's own plane, including the
  // descendants flattened onto it.
  // An element without a box draws nothing, including its descendants.
  let result =
    isHidden || opacity === 0 || node.style.display === 'none'
      ? ''
      : depsRenderResult +
        baseRenderResult +
        (isStackingContext
          ? paintStackingContext(
              stackingContext.layers,
              childrenRenderResult + outlineResult
            )
          : childrenRenderResult + outlineResult)

  // The filter applies to the element and its descendants as a group, then
  // its own clip path clips the filtered result, e.g. the edges of a blur.
  // https://drafts.fxtf.org/filter-effects-1/#placement
  const filters = computedStyle._filters as unknown as
    | (BackdropFilter | URLFilter)[]
    | undefined
  // Like in browsers, a reference to a missing SVG filter makes the whole list
  // ignored.
  const isMissing = (filter: BackdropFilter | URLFilter) =>
    filter.type === 'url' && !context.svgFilters?.get(filter.id)
  if (result && filters?.length && !filters.some(isMissing)) {
    // Filter functions are applied together, and SVG filters by themselves,
    // in order.
    let count = 0
    let functions: BackdropFilter[] = []
    const applyFunctions = () => {
      if (!functions.length) return
      // Descendants can be drawn anywhere, so the filter region is the image,
      // and what the filter draws into it from outside.
      const filterId = `satori_f-${id}${count ? `-${count}` : ''}`
      count++
      const expansion = getExpansion(functions)
      const primitives = functions
        .map((filter, index) =>
          buildFilterPrimitive(
            filter,
            index ? `${filterId}-${index - 1}` : 'SourceGraphic',
            `${filterId}-${index}`,
            false
          )
        )
        .join('')
      result =
        buildXMLString(
          'filter',
          {
            id: filterId,
            x: -expansion.left,
            y: -expansion.top,
            width:
              (computedStyle._viewportWidth as number) +
              expansion.left +
              expansion.right,
            height:
              (computedStyle._viewportHeight as number) +
              expansion.top +
              expansion.bottom,
            filterUnits: 'userSpaceOnUse',
            'color-interpolation-filters': 'sRGB',
          },
          primitives
        ) + buildXMLString('g', { filter: `url(#${filterId})` }, result)
      functions = []
    }
    for (const filter of filters) {
      if (filter.type !== 'url') {
        functions.push(filter)
        continue
      }
      applyFunctions()
      const filterId = `satori_f-${id}${count ? `-${count}` : ''}`
      count++
      // Like in browsers, the bounding box of the filter, e.g. for its default
      // region, is at least the border box of the element.
      const borderBox = buildXMLString('rect', {
        x: left,
        y: top,
        width,
        height,
        fill: 'none',
        transform: computedStyle.transform
          ? buildTransform(
              { left, top, width, height },
              computedStyle.transform,
              isInheritingTransform
            ) || undefined
          : undefined,
      })
      result =
        serializeSVGFilter(
          context.svgFilters!.get(filter.id)!,
          filterId,
          computedStyle.color as string,
          context.convertColors
        ) +
        buildXMLString('g', { filter: `url(#${filterId})` }, borderBox + result)
    }
    applyFunctions()
    if (hasClipPath && isInheritingTransform) {
      result = buildXMLString(
        'g',
        { 'clip-path': `url(#satori_cp-${id})` },
        result
      )
    }
  }

  if (result && transformState.projection && !isInheritingTransform) {
    result = context.projectPlane(result, {
      matrix: transformState.projection,
      node,
      left,
      top,
      style: computedStyle,
      id,
    })
  }

  // The root stacking context is isolated by the SVG itself.
  const isolate =
    isStackingContext &&
    stackingContext.hasBlending &&
    !!context.stackingContext
  if (result && (mixBlendMode || isolate || groupOpacity < 1)) {
    result = buildXMLString(
      'g',
      {
        opacity: groupOpacity < 1 ? groupOpacity : undefined,
        style:
          [
            mixBlendMode && `mix-blend-mode:${mixBlendMode}`,
            isolate && 'isolation:isolate',
          ]
            .filter(Boolean)
            .join(';') || undefined,
      },
      result
    )
  }

  if (plane) {
    plane.svg = result
    // The element establishing the 3D rendering context draws all of them.
    if (context.planes) return ''
    result = planes
      .sort((a, b) => a.depth - b.depth)
      .map(({ svg }) => svg)
      .join('')
  }

  if (layer) {
    layer.svg = result
    return ''
  }
  return result
}
